import { buildAppAuthUrl } from "@/auth/authLinks";
import type { InvitationDialogCopy } from "@/components/landing/content";
import { Link } from "@/components/ui/Link";
import { ResponsiveImage } from "@/components/ui/ResponsiveImage";
import { introHeroStyles as styles } from "@/styles/classes";
import type { Locale } from "@/types";
import { ArrowRight, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/** 加群二维码：与客服二维码同目录，未登记进 OSS 清单时回落到同源 public 路径。 */
const INVITATION_QR_SRC = "/landing/assets/contact/zesrin-invite-qr.png";
const INVITATION_X_URL = "https://x.com/ZesrinOfficial";

/**
 * 首页「申请早期体验」打开的获取邀请码弹窗。
 *
 * 中文展示加群二维码、英文指向官方 X 账号（微信码对英文访客无意义），底部两个入口
 * 始终是去注册与去登录，跳转沿用 `buildAppAuthUrl`（带当前语言和回跳规则）。
 */
export function InviteCodeDialog({
  copy,
  locale,
  onClose,
}: {
  copy: InvitationDialogCopy;
  locale: Locale;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const closeCallbackRef = useRef(onClose);

  useEffect(() => {
    closeCallbackRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeCallbackRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>("button, a[href]");
      if (!focusables?.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, []);

  const registerHref = buildAppAuthUrl("register", new URL(window.location.href));
  const loginHref = buildAppAuthUrl("login", new URL(window.location.href));

  return createPortal(
    <div
      className={styles.inviteBackdrop}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        aria-labelledby="studio-landing-invite-title"
        aria-modal="true"
        className={styles.inviteCard}
        ref={dialogRef}
        role="dialog"
      >
        <button
          aria-label={copy.close}
          className={styles.inviteClose}
          onClick={onClose}
          ref={closeButtonRef}
          type="button"
        >
          <X aria-hidden="true" />
        </button>
        <h2 id="studio-landing-invite-title">{copy.title}</h2>
        <p className={styles.inviteHint}>{copy.hint}</p>
        {locale === "zh" ? (
          <ResponsiveImage
            alt={copy.qrAlt ?? copy.title}
            className={styles.inviteImage}
            src={INVITATION_QR_SRC}
            width={232}
          />
        ) : (
          <a
            className={styles.inviteX}
            href={INVITATION_X_URL}
            rel="noreferrer"
            target="_blank"
          >
            {copy.xCta ?? INVITATION_X_URL}
            <ArrowRight aria-hidden="true" />
          </a>
        )}
        <div className={styles.inviteActions}>
          <Link className={styles.invitePrimary} href={registerHref}>
            {copy.registerCta}
            <ArrowRight aria-hidden="true" />
          </Link>
          <Link className={styles.inviteSecondary} href={loginHref}>
            {copy.loginCta}
          </Link>
        </div>
      </section>
    </div>,
    document.body,
  );
}
